import { clearAllBodyScrollLocks } from 'body-scroll-lock';
import { useRouter } from 'next/router';
import React, { createContext, ReactNode, useEffect, useState } from 'react';

import SideBarOverlay from '@/shared/components/atoms/SidebarOverlay';
import { TypographyVariant } from '@/shared/components/atoms/Typography/Typography.types';
import ModalOverlay from '@/shared/components/molecules/ModalOverlay';

type CloseModalFunction = (removeNumber?: number) => void;
type OnCloseFunction = (closeModal: CloseModalFunction) => void;

interface ModalOptions {
  title?: string | React.ReactNode;
  onClose?: OnCloseFunction;
  variant?: 'popup' | 'sidebar' | 'full';
  containerClassName?: string;
  dismissable?: boolean;
  // TODO: remove this
  titleVariant?: TypographyVariant;
}

interface ModalData {
  content: React.ReactNode;
  options?: ModalOptions;
}
type OpenModalFunction = (
  content: React.ReactNode,
  options?: ModalOptions
) => void;

interface ModalContextValue {
  openModal: OpenModalFunction;
  closeModal: CloseModalFunction;
}

export const ModalContext = createContext<ModalContextValue | undefined>({
  openModal: () => {
    return;
  },
  closeModal: () => {
    return;
  },
});

export const ModalProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const router = useRouter();
  const [modalStack, setModalStack] = useState<ModalData[]>([]);

  useEffect(() => {
    router.beforePopState(() => {
      closeModal();
      return true; // Allow the navigation
    });

    return () => {
      router.beforePopState(() => true); // Reset to default
    };
  }, [router]);
  const openModal: OpenModalFunction = (content, options) => {
    //  INFO: get immediate last state then push new modal
    setModalStack((prev) => [...prev, { content, options }]);
  };

  const closeModal = (removeNumber = 1) => {
    if (removeNumber <= 0) {
      return;
    }
    //INFO: get immediate last state
    setModalStack((prev) => {
      if (removeNumber > prev.length) {
        removeNumber = prev.length;
      }
      if (removeNumber === prev.length) clearAllBodyScrollLocks();
      return prev.slice(0, -removeNumber);
    });
  };

  const handleClose = (options?: ModalOptions) => {
    options?.onClose ? options.onClose(closeModal) : closeModal();
  };

  const contextValue: ModalContextValue = { openModal, closeModal };

  return (
    <ModalContext.Provider value={contextValue}>
      {children}
      {modalStack.map((modal, index) =>
        modal?.options?.variant === 'sidebar' ? (
          <SideBarOverlay
            key={index}
            title={modal?.options?.title || ''}
            closeModal={() => handleClose(modal?.options)}
            containerClassName={modal?.options?.containerClassName}
          >
            {modal.content}
          </SideBarOverlay>
        ) : (
          <ModalOverlay
            key={index}
            {...modal?.options}
            full={modal?.options?.variant === 'full'}
            open={true}
            closeModal={() => handleClose(modal?.options)}
          >
            {modal.content}
          </ModalOverlay>
        )
      )}
    </ModalContext.Provider>
  );
};
